import { createElement as rc, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import _HighchartsReact from 'highcharts-react-official';
import { useTheme } from 'styled-components';
import { Button, DatePicker, Label, Text, View, WaitScreen, fromTheme, styled } from 'lib_ui-primitives';
import metadataConfig from '@sstdev/lib_metadata-config';
import Chart from './Chart.limitAllSyncSizes';

const HighchartsReact = _HighchartsReact.default || _HighchartsReact;
const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const FIVE_MINUTES_MS = 5 * 60 * 1000;
const MAX_TOTAL_POINTS = 100000;
const POINT_LIMIT_MESSAGE = 'Choose a larger grouping, a shorter date range or narrower filters to display this chart.';
const { timeSeriesControls } = metadataConfig.config;
const Controls = styled(View)`
    flex-wrap: wrap;
    flex-shrink: 0;
    align-items: flex-end;
    gap: 12px;
    padding: 0 ${fromTheme('viewPadding')};
    color: ${fromTheme('defaultFontColor')};
`;
const DateField = styled(View)`
    flex-direction: column;
    flex: 1 1 220px;
    max-width: 280px;
`;
const ButtonField = styled(View)`
    flex-direction: column;
`;
const Buttons = styled(View)`
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
`;
const Plot = styled(View)`
    position: relative;
    width: 100%;
    min-width: 0;
    flex-grow: 1;
    align-items: flex-start;
    .highcharts-legend-item text {
        fill: ${fromTheme('defaultFontColor')};
    }
    .highcharts-legend-item:hover text {
        fill: ${fromTheme('charts', 'legendHoverColor')};
    }
    .highcharts-legend-item-hidden text {
        fill: ${fromTheme('disabledFontColor')} !important;
    }
    /* The shared bar/pie theme hides path strokes; a line needs its own stroke. */
    .highcharts-line-series .highcharts-graph {
        stroke-width: 2px;
        fill: none;
    }
    ${({ theme }) =>
        theme.chartColors
            .map(
                (color, index) => `
        .highcharts-line-series.highcharts-color-${index} .highcharts-graph,
        .highcharts-legend-item.highcharts-color-${index} .highcharts-graph {
            stroke: ${color} !important;
        }
    `
            )
            .join('')}
`;

/** Fill empty buckets with zero in the same timezone used by the server.
 * Calendar days can span 23 or 25 hours; advancing by 24 hours would shift them.
 */
export function timeSeriesPoints(
    data,
    from,
    to,
    interval,
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
) {
    const step = interval === 'day' ? DAY_MS : interval === 'fiveMinutes' ? FIVE_MINUTES_MS : HOUR_MS;
    if (!validRange({ from, to }, interval)) return [];
    const time = new Highcharts.Time({ timezone });
    const parts = time.toParts(from);
    let at =
        interval === 'day'
            ? time.makeTime(...parts.slice(0, 3))
            : from - ((parts[4] % (interval === 'fiveMinutes' ? 5 : 60)) * 60000 + parts[5] * 1000 + parts[6]);
    const counts = new Map();
    for (const row of data) {
        const bucket = Date.parse(typeof row._id?.title === 'object' ? row._id.title?.at : row._id?.title);
        if (!Number.isFinite(bucket)) continue;
        counts.set(bucket, (counts.get(bucket) || 0) + row.count);
    }
    const points = [];
    while (at <= to && points.length < timeSeriesControls.maxBuckets) {
        points.push([at, counts.get(at) || 0]);
        if (interval === 'day') {
            const [year, month, day] = time.toParts(at);
            at = time.makeTime(year, month, day + 1);
        } else at += step;
    }
    return points;
}

export function timeSeries(
    data,
    from,
    to,
    interval,
    timezone,
    valueTitle,
    seriesProperty,
    colorCount,
    colors = new Map()
) {
    const groups = new Map();
    for (const row of data) {
        const name = seriesProperty ? String(row._id?.title?.series ?? 'Unassigned') : valueTitle;
        if (!groups.has(name)) groups.set(name, []);
        groups.get(name).push(row);
    }
    // Sparse results still expand to a full timeline for every series.
    // Check the combined size before allocating those points.
    const buckets = timeSeriesPoints([], from, to, interval, timezone).length;
    if (buckets * groups.size > MAX_TOTAL_POINTS) throw new RangeError(POINT_LIMIT_MESSAGE);
    return [...groups]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([name, rows]) => {
            if (!colors.has(name)) {
                let index = [...name].reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) >>> 0, 0) % colorCount;
                // Reuse each series' color across filters; avoid duplicate colors
                // while this chart still has unused colors in its theme palette.
                const used = new Set(colors.values());
                while (used.size < colorCount && used.has(index)) index = (index + 1) % colorCount;
                colors.set(name, index);
            }
            return {
                id: name,
                name,
                colorIndex: colors.get(name),
                data: timeSeriesPoints(rows, from, to, interval, timezone)
            };
        });
}

function LineChart({
    data,
    dataFilters,
    isLoading,
    hNode: { id, valueTitle = 'Records', seriesProperty },
    from: requestedFrom,
    to: requestedTo,
    interval: requestedInterval,
    timezone,
    seriesColors,
    focusedSeries,
    setFocusedSeries
}) {
    // While fetching, keep both the previous points and their original axes.
    const from = dataFilters ? Date.parse(dataFilters.dateRange.fromValue) : requestedFrom;
    const to = dataFilters ? Date.parse(dataFilters.dateRange.toValue) : requestedTo;
    const interval = dataFilters?.groupBy.interval || requestedInterval;
    const theme = useTheme();
    const chartRef = useRef();
    const plotRef = useRef();
    useLayoutEffect(() => {
        // A newly loaded range should show its full span, rather than the old zoom.
        const chart = chartRef.current?.chart;
        const axis = chart?.xAxis[0];
        if (axis?.userMin !== undefined || axis?.userMax !== undefined) chart.zoomOut();
    }, [from, to, interval]);
    const plot = useMemo(() => {
        try {
            return { series: timeSeries(
                data, from, to, interval, timezone, valueTitle, seriesProperty, theme.chartColors.length, seriesColors
            ) };
        } catch (error) {
            if (!(error instanceof RangeError) || error.message !== POINT_LIMIT_MESSAGE) throw error;
            return { series: [], error: error.message };
        }
    }, [data, from, to, interval, timezone, valueTitle, seriesProperty, theme.chartColors.length, seriesColors]);
    const options = useMemo(() => {
        const { charts, chartColors } = theme;
        const labels = { style: { color: charts.axes.labelColor, ...charts.axes.labelFont } };
        const { series } = plot;
        const hasFocusedSeries = series.some(row => row.id === focusedSeries);
        return {
            chart: { type: 'line', backgroundColor: 'transparent', styledMode: true, zooming: { type: 'x' } },
            title: { text: '' },
            time: { timezone },
            colors: chartColors,
            credits: { enabled: false },
            legend: {
                enabled: !!seriesProperty,
                align: 'left',
                itemStyle: { color: theme.defaultFontColor },
                itemHiddenStyle: { color: theme.disabledFontColor },
                itemHoverStyle: { color: charts.legendHoverColor },
                events: {
                    itemClick: event => {
                        // Click a series to focus it; click it again to restore all lines.
                        const name = event.legendItem.name;
                        setFocusedSeries(name === focusedSeries ? null : name);
                        return false;
                    }
                }
            },
            xAxis: { type: 'datetime', labels, crosshair: true, title: { text: timezone, style: labels.style } },
            yAxis: {
                min: 0,
                allowDecimals: false,
                title: { text: valueTitle, style: labels.style },
                gridLineColor: charts.axes.gridLineColor,
                gridLineWidth: charts.axes.gridLineWidth,
                labels
            },
            tooltip: {
                shared: true,
                xDateFormat: interval === 'day' ? '%A, %b %e, %Y' : '%b %e, %Y %I:%M %p',
                pointFormat:
                    '<span class="highcharts-color-{series.colorIndex}">●</span> {series.name}: <b>{point.y:,.0f}</b><br/>'
            },
            plotOptions: { line: { marker: { enabled: false }, lineWidth: 2 } },
            series: series.map(row => ({ ...row, visible: !hasFocusedSeries || row.id === focusedSeries }))
        };
    }, [plot, interval, valueTitle, seriesProperty, timezone, theme, focusedSeries, setFocusedSeries]);
    useLayoutEffect(() => {
        if (!plotRef.current) return;
        // Controls can wrap onto another row. Size the SVG to the remaining plot
        // space so its axes and legend are not clipped by the chart card.
        const observer = new ResizeObserver(([entry]) => {
            const { width, height } = entry.contentRect;
            if (width > 0 && height > 0) chartRef.current?.chart.setSize(width, height, false);
        });
        observer.observe(plotRef.current);
        return () => observer.disconnect();
    }, [plot.error]);
    if (plot.error) return rc(Text, { role: 'alert' }, plot.error);
    return rc(
        Plot,
        { ref: plotRef, 'aria-busy': isLoading },
        rc(WaitScreen, { id: `${id}-updating`, waiting: isLoading },
            rc(HighchartsReact, {
                ref: chartRef,
                highcharts: Highcharts,
                options,
                updateArgs: [true, true],
                containerProps: { style: { width: '100%', height: '100%' } }
            })
        )
    );
}

/** Count records over time using the normal aggregate endpoint and shared page filters.
 * Metadata names the timestamp and optional series field on any relation.
 * Only counts are fetched. Changing the range or grouping never queries a device.
 */
export default function TimeSeriesChart(props) {
    const { dateRanges, groupings, defaultRange, defaultGrouping } = props.hNode;
    const initialRange = timeSeriesControls.ranges.find(({ value }) => value === defaultRange);
    if (
        !Array.isArray(dateRanges) || !dateRanges.length ||
        !Array.isArray(groupings) || !groupings.length ||
        !dateRanges.includes(defaultRange) || !groupings.includes(defaultGrouping) ||
        dateRanges.some(value => !timeSeriesControls.ranges.some(range => range.value === value)) ||
        groupings.some(value => !timeSeriesControls.groupings.some(grouping => grouping.value === value)) ||
        !initialRange || !validRange({ from: 0, to: initialRange.duration }, defaultGrouping)
    ) {
        return rc(Text, { role: 'alert' }, 'Configure this chart’s date ranges and grouping in Metadata Creator.');
    }
    return rc(ConfiguredTimeSeriesChart, props);
}

function ConfiguredTimeSeriesChart(props) {
    const {
        hNode: { id, timeProperty, seriesProperty, dateRanges, groupings, defaultRange, defaultGrouping }
    } = props;
    const initialDuration = timeSeriesControls.ranges.find(({ value }) => value === defaultRange).duration;
    const availableRanges = timeSeriesControls.ranges.filter(({ value }) => dateRanges.includes(value));
    const availableGroupings = timeSeriesControls.groupings.filter(({ value }) => groupings.includes(value));
    const [range, setRange] = useState(() => {
        const to = Date.now();
        return { from: to - initialDuration, to };
    });
    const [interval, setInterval] = useState(defaultGrouping);
    const [selectedDuration, setSelectedDuration] = useState(initialDuration);
    const [focusedSeries, setFocusedSeries] = useState(null);
    const seriesColors = useRef(new Map()).current;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Keep the last valid query while the user clears or edits a date field.
    const [draft, setDraft] = useState(range);
    const setDate = (field, value) => {
        const next = { ...draft, [field]: value ? Date.parse(value) : null };
        setSelectedDuration(null);
        setDraft(next);
        if (validRange(next, interval)) setRange(next);
    };
    const setDuration = (duration, grouping) => {
        const to = Date.now();
        const next = { from: to - duration, to };
        setDraft(next);
        setRange(next);
        setInterval(grouping);
        setSelectedDuration(duration);
    };
    // Prefer the preset's useful grouping, then a configured grouping that fits
    // the range. Never enable a grouping the metadata author excluded.
    const groupingFor = ({ duration, interval: preferred }) => {
        const fits = value => validRange({ from: 0, to: duration }, value);
        return [preferred, defaultGrouping, ...groupings].find(value => groupings.includes(value) && fits(value));
    };
    const additionalFilters = {
        dateRange: { propertyName: timeProperty, fromValue: new Date(range.from), toValue: new Date(range.to) },
        groupBy: { propertyName: timeProperty, interval, timezone, ...(seriesProperty ? { seriesProperty } : {}) }
    };
    const invalidRange = !validRange(draft, interval);
    const controls = rc(
        Controls,
        null,
        ...['from', 'to'].map((field, sequence) =>
            rc(
                DateField,
                { key: field },
                rc(Label, { htmlFor: `${id}-${field}` }, field === 'from' ? 'From' : 'To'),
                rc(DatePicker, {
                    id: `${id}-${field}`,
                    sequence,
                    value: draft[field],
                    format: 'PP p',
                    onChange: value => setDate(field, value)
                })
            )
        ),
        rc(
            ButtonField,
            null,
            rc(Label, null, 'Date range'),
            rc(
                Buttons,
                { role: 'group', 'aria-label': 'Date range' },
                ...availableRanges.map(preset =>
                    rc(Button, {
                        key: preset.value,
                        text: preset.label,
                        value: preset.label,
                        title: `Last ${preset.value === 'oneHour' ? 'hour' : preset.label}`,
                        variant: selectedDuration === preset.duration ? 'primary' : 'secondary',
                        'aria-pressed': selectedDuration === preset.duration,
                        size: 'sm',
                        disabled: !groupingFor(preset),
                        onClick: () => setDuration(preset.duration, groupingFor(preset))
                    })
                )
            )
        ),
        rc(
            ButtonField,
            null,
            rc(Label, null, 'Group by'),
            rc(
                Buttons,
                { role: 'group', 'aria-label': 'Group by' },
                ...availableGroupings.map(({ value, label }) =>
                    rc(Button, {
                        key: value,
                        text: label,
                        value: label,
                        variant: interval === value ? 'primary' : 'secondary',
                        size: 'sm',
                        'aria-pressed': interval === value,
                        disabled: !validRange(draft, value),
                        onClick: () => {
                            setInterval(value);
                            setRange(draft);
                        }
                    })
                )
            )
        ),
        invalidRange &&
            rc(Text, { role: 'alert' }, 'Choose a valid date range or a larger grouping.')
    );
    return rc(Chart, {
        ...props,
        hNode: { ...props.hNode, limitResults: 0 },
        nestedComponent: LineChart,
        additionalFilters,
        preserveGroups: true,
        keepPreviousData: true,
        seriesColors,
        focusedSeries,
        setFocusedSeries,
        timezone,
        controls,
        ...range,
        interval
    });
}

function validRange({ from, to }, interval) {
    const duration = timeSeriesControls.groupings.find(({ value }) => value === interval)?.duration;
    return (
        Number.isFinite(from) &&
        Number.isFinite(to) &&
        from <= to &&
        (to - from) / duration < timeSeriesControls.maxBuckets
    );
}
